import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import ContactFooter from '@/pages/home/components/ContactFooter';
import { REGULATION_META } from './data';
import type { RegulationItem } from './data';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

interface CategoryText {
  id: string;
  label: string;
  title: string;
  badge: string;
  items: RegulationItem[];
}

interface Category extends CategoryText {
  icon: string;
  image: string;
}

function CategorySection({ category }: { category: Category }) {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      id={category.id}
      className="reveal-on-scroll animate-fade-in-up scroll-mt-28"
    >
      {/* Section Label */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50 text-teal-500">
          <i className={`${category.icon} text-xl`} />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{category.title}</h2>
        <span className="hidden sm:inline-block bg-teal-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ml-auto">
          {category.badge}
        </span>
      </div>

      {/* Image + Items Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        {/* Image Card */}
        <div className="lg:col-span-2 relative rounded-2xl overflow-hidden h-[280px] lg:h-full min-h-[360px]">
          <img
            src={category.image}
            alt={category.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <span className="inline-block sm:hidden bg-teal-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
              {category.badge}
            </span>
            <h3 className="text-white font-bold text-xl leading-snug">{category.title}</h3>
          </div>
        </div>

        {/* Items */}
        <div className="lg:col-span-3 flex flex-col gap-4">
          {category.items.map((item) => (
            <div
              key={item.name}
              className="bg-teal-50 border border-gray-200 rounded-2xl p-5 hover:border-teal-200 transition-colors duration-200"
            >
              <div className="flex flex-wrap items-start gap-3 mb-3">
                <h4 className="font-semibold text-gray-900 text-base">{item.name}</h4>
              </div>
              <div className="flex flex-wrap gap-4 mb-3">
                <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                  <i className="ri-government-line text-gray-400" />
                  {item.authority}
                </div>
                <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                  <i className="ri-refresh-line text-gray-400" />
                  {item.renewal}
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed border-t border-gray-200 pt-3">
                {item.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function RegulationsPage() {
  const location = useLocation();
  const { t } = useTranslation();

  const rawCategories = t('regulations.categories', { returnObjects: true }) as CategoryText[];
  const categories: Category[] = rawCategories.map((cat) => ({
    ...cat,
    icon: REGULATION_META[cat.id].icon,
    image: REGULATION_META[cat.id].image,
  }));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  return (
    <main className="overflow-x-hidden min-h-screen bg-white">
      <Navbar />

      {/* Hero / Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-teal-50 text-teal-600 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
              {t('regulations.tag')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              {t('regulations.title1')}<br />
              <span className="text-teal-500">{t('regulations.title2')}</span>
            </h1>
            <p className="mt-5 text-gray-500 text-base md:text-lg leading-relaxed">
              {t('regulations.subtitle')}
            </p>
          </div>

          {/* Quick Jump Links */}
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  const el = document.getElementById(cat.id);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-white border border-gray-200 text-gray-700 hover:border-teal-300 hover:text-teal-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className={cat.icon} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Categories — Full Width, No Sidebar */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col gap-20 md:gap-28">
            {categories.map((category) => (
              <CategorySection key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-3 bg-teal-50 border border-gray-200 rounded-2xl p-5 max-w-4xl mx-auto">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-teal-500">
              <i className="ri-information-line text-xl" />
            </div>
            <p className="text-gray-900 text-sm leading-relaxed">
              <strong>{t('regulations.disclaimerTitle')}</strong> {t('regulations.disclaimerText')}
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {t('regulations.ctaTitle')}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto mb-8">
            {t('regulations.ctaText')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#consultation"
              className="bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200 whitespace-nowrap inline-flex items-center gap-2"
            >
              {t('regulations.bookSession')}
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/"
              className="text-gray-600 hover:text-teal-500 text-sm font-semibold px-6 py-3 transition-colors duration-200 whitespace-nowrap inline-flex items-center gap-2"
            >
              {t('regulations.backHome')}
              <i className="ri-home-line" />
            </Link>
          </div>
        </div>
      </section>
      <ContactFooter />
    </main>
  );
}